import React from 'react';
import { Link } from 'react-router-dom';
import { NavBar, Footer } from '../../components/redesign/shared.jsx';
import SEO from '../../components/SEO.jsx';
import { useCurrency } from '../../utils/currency.js';

const SERVICES = [
  {
    id: 'implementation',
    route: '/services/suitecrm-implementation',
    num: 'I.',
    tag: 'Implementation',
    title: 'Deploy SuiteCRM, production-ready, in weeks.',
    body: 'We install, configure and harden SuiteCRM on your infrastructure — or on managed hosting we run for you. Every module, every role, every integration point set up against a real rollout plan, not a sandbox.',
    includes: [
      'Discovery of your existing sales, service and ops workflows',
      'SuiteCRM 8 / 7 deployment on your chosen cloud or on-prem',
      'Module configuration: leads, accounts, opportunities, cases, quotes',
      'Role-based access, territory rules, team hierarchies',
      'Data migration from HubSpot, Salesforce, Zoho, Pipedrive or Excel',
      'User training, admin handover, go-live support',
    ],
    outcome: 'A live CRM your team actually opens. Week six, not quarter three.',
  },
  {
    id: 'customisation',
    route: '/services/suitecrm-customisation',
    num: 'II.',
    tag: 'Customisation',
    title: 'Modules and workflows shaped to how you actually sell.',
    body: "Most CRMs ask you to bend. We don't. We build new modules, custom fields, logic hooks and tailored layouts so the system mirrors the way your best reps already work — not a vendor's idea of how they should.",
    includes: [
      'Custom modules and relationship graphs',
      'Workflow rules, approvals, escalation chains',
      'Custom fields, dependent dropdowns, validation logic',
      'Role-based Studio layouts and reports',
      'White-labelled theming that matches your brand',
      'Custom dashboards and pipeline views',
    ],
    outcome: 'The CRM adapts to your process. Not the other way around.',
  },
  {
    id: 'integration',
    route: '/services/crm-integration',
    num: 'III.',
    tag: 'Integration',
    title: 'Make the CRM the spine of your operation.',
    body: "A CRM that doesn't talk to your ERP, your finance system, your phone tree or WhatsApp is a CRM half-used. We build reliable, observable integrations — not fragile Zapier chains — so your data lives in one place.",
    includes: [
      'ERP connectors: NetSuite, Odoo, SAP, Tally',
      'Payment & invoicing: Stripe, Razorpay, QuickBooks, Zoho Books',
      'Communications: WhatsApp Business API, Twilio, Exotel, RingCentral',
      'Email: Gmail, Outlook, Mailgun, Sendgrid — two-way sync',
      'Data warehouse: Snowflake, BigQuery, Postgres — ETL and CDC',
      'Custom APIs with auth, retries, observability',
    ],
    outcome: 'One source of truth. Every team working off the same data.',
  },
  {
    id: 'ai',
    route: '/services/ai-workflow-automation',
    num: 'IV.',
    tag: 'AI Workflow Automation',
    title: 'AI that stays inside your system of record.',
    body: 'Most AI CRM add-ons ship your pipeline to a third party. We design scoring, routing and follow-up automations that run on your infrastructure — or against models you control. Your data stays yours.',
    includes: [
      'Lead scoring and fit models trained on your won/lost data',
      'Smart routing and territory assignment',
      'Automated follow-up sequences with human-in-the-loop review',
      'Meeting notes, call summaries and CRM hygiene tasks',
      'Self-hosted LLMs or BYO-API-key integration',
      'Full audit trail — every AI-written field is traceable',
    ],
    outcome: 'Your reps spend more time selling. Your data never leaves.',
  },
  {
    id: 'support',
    route: '/services/suitecrm-support',
    num: 'V.',
    tag: 'Support & Maintenance',
    title: 'The engineering team behind your CRM, on retainer.',
    body: 'Open source without a steward is a liability. With us on retainer you get upgrades, security patches, new feature work and a predictable monthly — not another subscription that grows with every seat.',
    includes: [
      'SuiteCRM version upgrades and security patches',
      'Performance tuning, indexing, query optimisation',
      'Managed hosting: backups, monitoring, uptime SLAs',
      'New feature work on a rolling backlog',
      'Incident response and root-cause analysis',
      'Quarterly roadmap reviews with your leadership',
    ],
    outcome: 'Predictable cost. Unpredictable problems handled.',
  },
  {
    id: 'opensource',
    route: '/opensource',
    num: 'VI.',
    tag: 'Docker & Dev Resources',
    title: 'The open-source tooling we use, given back.',
    body: "We maintain production-grade Docker images for SuiteCRM, an MCP server for AI agents, and a growing set of developer utilities — all open source. If your team wants to self-serve, these are the foundations we'd hand them.",
    includes: [
      'SuiteCRM production Docker images — PHP-FPM, Nginx, Elastic',
      'MCP server for SuiteCRM — for AI-agent-friendly CRM access',
      'Helm charts for Kubernetes deployments',
      'CLI tooling for backups, migrations, diagnostics',
      'Reference implementations and integration templates',
      'Issue support via GitHub, community & paid tiers',
    ],
    outcome: 'Your team can ship with confidence — alone, or with us behind them.',
  },
];

const FAQ = [
  {
    q: 'How long does a typical implementation take?',
    a: 'Most fixed-scope builds land in 8–14 weeks from signed scope to go-live. Complex migrations with ERP integrations stretch to 16–20 weeks. We commit to a date at the end of discovery — and we hit them.',
  },
  {
    q: 'Where does our data actually live?',
    a: "Wherever you want. Your cloud account (AWS, Azure, GCP), your on-prem servers, or managed hosting we run for you in your preferred region. We don't touch your data without an audit trail.",
  },
  {
    q: 'Do we really own the code?',
    a: 'Yes. SuiteCRM is AGPL-licensed, and all customisations we build are yours — committed to a repository you own from day one. If we part ways, you take everything with you.',
  },
  {
    q: 'How does this compare to Salesforce or HubSpot cost-wise?',
    a: "For teams above 40 seats, our clients typically save 50–70% over five years vs. per-seat SaaS. We'll model your specific TCO in the first call — no hand-waving.",
  },
  {
    q: 'What if we already have a CRM and want to migrate?',
    a: "Half our work is migrations. We've moved clients off Salesforce, HubSpot, Zoho, Pipedrive, Microsoft Dynamics and homegrown systems. Discovery includes a data-quality audit of your existing stack.",
  },
  {
    q: 'Do you support us post go-live?',
    a: "Yes, via the Build + Run retainer. It's how ~80% of our clients engage with us long-term. You can also work with us purely for the build and take the code elsewhere.",
  },
];

export function ServicesA({ onNav }) {
  const { price } = useCurrency();
  return (
    <div className="pageA">
      <SEO
        title="Solutions — Six services, one CRM you own | Aakeeo"
        description="SuiteCRM implementation, customisation, integration, AI workflow automation, support, and open-source dev resources. Six services, one roof, zero subscriptions."
        keywords={['SuiteCRM implementation', 'SuiteCRM customisation', 'CRM integration', 'AI workflow automation', 'SuiteCRM support', 'open source CRM']}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Solutions' },
        ]}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Open-source CRM implementation & engineering',
            provider: { '@type': 'Organization', name: 'Aakeeo Technologies', url: 'https://www.aakeeo.com' },
            areaServed: ['India', 'UK', 'US', 'Australia', 'France'],
            description: 'End-to-end SuiteCRM implementation, customisation, integration, AI workflow automation, and ongoing support.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        ]}
      />
      <NavBar page="solutions" onNav={onNav} />

      <main>

      {/* HERO */}
      <section className="solA-hero">
        <span className="eyebrowA">Solutions</span>
        <h1>
          Six services. One outcome: a CRM you <em>own</em>.
        </h1>
        <p className="solA-hero-sub">
          We engineer custom CRMs on SuiteCRM and open foundations — end to end. From the first discovery call to the tenth year of support, you work with the same team. One roof. One invoice. Zero subscriptions.
        </p>
        <div className="solA-hero-cta">
          <button className="btnA-primary" onClick={() => onNav('contact')}>Book a strategy call</button>
          <a className="btnA-ghost" href="#implementation">Jump to services ↓</a>
        </div>
      </section>

      {/* INDEX */}
      <section className="solA-index">
        <div className="solA-index-head">
          <span className="eyebrowA">Index</span>
          <h2>What we deliver</h2>
        </div>
        <ol className="solA-index-list">
          {SERVICES.map((s) => (
            <li key={s.id}>
              <Link to={s.route}>
                <span className="solA-index-num">{s.num}</span>
                <span className="solA-index-tag">{s.tag}</span>
                <span className="solA-index-dot" />
                <span className="solA-index-arrow">→</span>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      {/* SERVICES */}
      {SERVICES.map((s, i) => (
        <section key={s.id} id={s.id} className={'solA-svc' + (i % 2 === 1 ? ' solA-svc--alt' : '')}>
          <div className="solA-svc-inner">
            <div className="solA-svc-left">
              <span className="solA-svc-num">{s.num}</span>
              <span className="solA-svc-tag">{s.tag}</span>
            </div>
            <div className="solA-svc-mid">
              <h2>{s.title}</h2>
              <p className="solA-svc-lead">{s.body}</p>
              <div className="solA-svc-outcome">
                <span className="solA-svc-outcome-label">Outcome</span>
                <p>{s.outcome}</p>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link className="btnA-primary btnA-primary--sm" to={s.route}>
                  Read the full service →
                </Link>
                <button className="btnA-ghost" onClick={() => onNav('contact')} style={{ padding: '8px 14px', fontSize: 13 }}>
                  Talk to us about {s.tag}
                </button>
              </div>
            </div>
            <div className="solA-svc-right">
              <span className="solA-svc-right-label">Includes</span>
              <ul>
                {s.includes.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      {/* ENGAGEMENT MODELS */}
      <section className="solA-models">
        <div className="solA-models-head">
          <span className="eyebrowA">Engagement</span>
          <h2>Three ways to work with us.</h2>
        </div>
        <div className="solA-models-grid">
          <article className="solA-model">
            <div className="solA-model-head">
              <span className="solA-model-num">01</span>
              <h3>Fixed-scope build</h3>
            </div>
            <p className="solA-model-price">One-time · <strong>{price({ usd: 'from $28k', inr: '₹10–15L' })}</strong></p>
            <p className="solA-model-body">Scoped implementation and customisation on a fixed price, fixed timeline. You get a production-ready CRM and the code to go with it.</p>
            <ul>
              <li>Discovery, architecture, build, migration</li>
              <li>Weekly demos, milestone-based invoicing</li>
              <li>60-day warranty post go-live</li>
            </ul>
            <Link className="solA-model-cta" to="/contact">Best for first engagements →</Link>
          </article>
          <article className="solA-model solA-model--featured">
            <div className="solA-model-head">
              <span className="solA-model-num">02</span>
              <h3>Build + Run retainer</h3>
              <span className="solA-model-badge">Most teams choose this</span>
            </div>
            <p className="solA-model-price">Build + <strong>{price({ usd: '$2.4k', inr: '₹1.5–2L' })}</strong>/mo retainer</p>
            <p className="solA-model-body">Fixed-scope build followed by a rolling retainer — managed hosting, upgrades, and a monthly budget for new feature work. One invoice, one team, no per-seat maths.</p>
            <ul>
              <li>Managed hosting &amp; monitoring</li>
              <li>20h / mo of engineering, rolling over</li>
              <li>Quarterly roadmap &amp; leadership review</li>
            </ul>
            <Link className="solA-model-cta" to="/contact">Our default recommendation →</Link>
          </article>
          <article className="solA-model">
            <div className="solA-model-head">
              <span className="solA-model-num">03</span>
              <h3>Embedded engineering</h3>
            </div>
            <p className="solA-model-price">Dedicated team · <strong>{price({ usd: 'from $18k', inr: 'from ₹5L' })}</strong>/mo</p>
            <p className="solA-model-body">For teams mid-migration or mid-platform-build. A dedicated engineer or two, working as part of your team, reporting into your product or IT lead.</p>
            <ul>
              <li>1–3 dedicated engineers</li>
              <li>In your stand-ups, your repo, your Slack</li>
              <li>30/60/90-day engagements</li>
            </ul>
            <Link className="solA-model-cta" to="/contact">Best for scale-ups →</Link>
          </article>
        </div>
      </section>

      {/* FAQ */}
      <section className="solA-faq">
        <div className="solA-faq-head">
          <span className="eyebrowA">Details</span>
          <h2>Questions we get in the first call.</h2>
        </div>
        <div className="solA-faq-list">
          {FAQ.map((item, i) => (
            <details key={i} className="solA-faq-item">
              <summary>
                <span className="solA-faq-num">{String(i + 1).padStart(2, '0')}</span>
                <span>{item.q}</span>
                <span className="solA-faq-chev">＋</span>
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="ctaA">
        <div className="ctaA-inner">
          <h2>Let&apos;s scope your&nbsp;CRM.</h2>
          <p>30 minutes. No slide deck. We&apos;ll map your current stack, flag the three biggest risks, and tell you if we&apos;re the right fit — even if the answer is no.</p>
          <div className="ctaA-buttons">
            <button className="btnA-primary" onClick={() => onNav('contact')}>Book a strategy call</button>
            <button className="btnA-ghost" onClick={() => onNav('blog')}>See how clients use us →</button>
          </div>
        </div>
      </section>

      </main>

      <Footer onNav={onNav} />
    </div>
  );
}
